'use client'

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CookiePolicyPageData } from '@/lib/api/strapi/browser-cookies/cookies.types'
import RichText from '../rich-text'
import {
  setBrowserCookie,
  CookiePreferences,
} from '@/lib/utils/browser-cookies'
import { Dictionary } from '@/localization/dictionary'

type PreferencesDialogProps = {
  onAccept: () => void
  onDecline: () => void
  data: CookiePolicyPageData['privacy_policy']
  dict: Dictionary['cookieBanner']
}

export function PreferencesDialog({
  onAccept,
  onDecline,
  data,
  dict,
}: PreferencesDialogProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    marketing: false,
    externalMedia: false,
    statistic: false,
  })

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'essential') return // Essential can't be toggled

    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleSavePreferences = () => {
    // Save preferences as cookie
    setBrowserCookie('cookie-preferences', JSON.stringify(preferences))
    setBrowserCookie('cookie-consent', 'true')
    onAccept()
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link">{dict.managePreferences}</Button>
      </DialogTrigger>
      <DialogContent aria-describedby={undefined} className="flex max-w-[45.25rem] flex-col gap-6 p-12">
        <DialogHeader>
          <DialogTitle className="text-center font-serif text-4xl font-normal uppercase italic">
            {data.title}
          </DialogTitle>
        </DialogHeader>

        <RichText content={data.description} />

        <div className="container grid max-w-xl grid-cols-2 gap-8 p-4">
          <div className="flex items-center justify-center space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Switch
                  id="essential"
                  checked={preferences.essential}
                  disabled
                />
                <Label htmlFor="essential" className="font-normal">
                  {data.essential}
                </Label>
              </div>

              <div className="flex items-center gap-4">
                <Switch
                  id="externalMedia"
                  checked={preferences.externalMedia}
                  onCheckedChange={() =>
                    handlePreferenceChange('externalMedia')
                  }
                />
                <Label htmlFor="externalMedia" className="font-normal">
                  {data.external_media}
                </Label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={() => handlePreferenceChange('marketing')}
                />
                <Label htmlFor="marketing" className="font-normal">
                  {data.marketing}
                </Label>
              </div>

              <div className="flex items-center gap-4">
                <Switch
                  id="statistic"
                  checked={preferences.statistic}
                  onCheckedChange={() => handlePreferenceChange('statistic')}
                />
                <Label htmlFor="statistic" className="font-normal">
                  {data.statistic}
                </Label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-4 border-t pt-6 sm:flex-row sm:justify-between sm:space-y-0">
          <Button variant="link" className="w-full sm:w-auto">
            {dict.preferences}
          </Button>
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-8 sm:space-y-0">
            <Button
              variant="outline"
              onClick={onDecline}
              className="w-full sm:w-auto md:mr-8"
            >
              {dict.rejectNonEssential}
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="w-full sm:w-auto"
            >
              {dict.accept}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
